"use client";

import { Sparkles } from "lucide-react";
import { PandaLogo } from "@/components/panda-logo";

export interface CreditsResetAction {
  action: string;
  prompt: string;
}

/**
 * Shared "Your credits are back." card (PANDA-811). Single source of the copy +
 * style so every surface that surfaces the daily-reset moment looks identical:
 *   • the project-page chat timeline (components/chat/message.tsx, kind
 *     "credits-reset"), and
 *   • the logged-in home hero (app/home-client.tsx) — where a returning user who
 *     exhausted their daily cap actually lands.
 *
 * Presentational only: the caller owns detection (hasDailyCreditsReset), the
 * once-per-window localStorage guard, and what a chip does when tapped (run the
 * prompt in chat vs. start a build from the composer). Chips render only when an
 * `onSelect` handler is supplied.
 */
export function CreditsResetCard({
  actions,
  onSelect,
}: {
  actions: CreditsResetAction[];
  onSelect?: (action: CreditsResetAction) => void;
}) {
  return (
    <div className="flex justify-start gap-2.5 px-4 py-2">
      <PandaLogo size="xs" className="mt-1 h-5 w-5" />
      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex items-center gap-1.5">
          <Sparkles className="h-4 w-4 shrink-0 text-orange-500" />
          <p className="text-[15px] font-semibold leading-snug text-zinc-900 dark:text-white">
            Your credits are back.
          </p>
        </div>
        <p className="text-[13px] leading-relaxed text-zinc-600 dark:text-zinc-400">
          Your daily credits have reset, so you&apos;re ready to keep building.
          Here are a few ways to pick things up.
        </p>
        {actions.length > 0 && onSelect && (
          <div className="mt-0.5 flex flex-wrap gap-1.5">
            {actions.map((a) => (
              <button
                key={a.action}
                onClick={() => onSelect(a)}
                className="rounded-full px-3 py-1.5 text-[12px] font-medium text-zinc-500 dark:text-zinc-400 bg-zinc-100 dark:bg-white/[0.04] ring-1 ring-black/[0.04] dark:ring-white/[0.04] transition-all hover:bg-zinc-200 dark:hover:bg-white/[0.08] hover:text-zinc-700 dark:hover:text-zinc-200 active:scale-[0.97]"
              >
                {a.action}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
